import { getImageSize, fitWidthKeepAspect } from '@/src/utils';

export function extFromFileNameOrType(name: string, type: string): string {
    const byName = (name?.split('.').pop() || '').toLowerCase();
    if (byName) return '.' + byName;
    const t = (type || '').toLowerCase();
    if (t.includes('png')) return '.png';
    if (t.includes('jpeg') || t.includes('jpg')) return '.jpg';
    if (t.includes('gif')) return '.gif';
    if (t.includes('webp')) return '.webp';
    return '.bin';
}

export async function resizeToMax(
    blob: Blob,
    mime: string = blob.type || 'image/png'
): Promise<Blob> {
    const { width, height } = await getImageSize(blob);
    const target = fitWidthKeepAspect(width, height, 1000, 600);

    if (target.width === width && target.height === height) {
        return blob; // no resize necessary
    }

    // draw into canvas
    const useOffscreen = 'OffscreenCanvas' in window;
    const canvas: HTMLCanvasElement | OffscreenCanvas = useOffscreen
        ? new (window as any).OffscreenCanvas(target.width, target.height)
        : Object.assign(document.createElement('canvas'), {
              width: target.width,
              height: target.height,
          });

    const ctx = (canvas as any).getContext('2d') as
        | OffscreenCanvasRenderingContext2D
        | CanvasRenderingContext2D;
    const bmp =
        'createImageBitmap' in window
            ? await createImageBitmap(blob)
            : await loadImageBitmapFallback(blob);
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.drawImage(bmp as any, 0, 0, target.width, target.height);
    (bmp as any).close?.();

    // canvas to Blob
    if ('convertToBlob' in canvas) {
        return await (canvas as any).convertToBlob({ type: mime, quality: 0.92 });
    }
    return await new Promise<Blob>((resolve) => {
        (canvas as HTMLCanvasElement).toBlob((b) => resolve(b || new Blob()), mime, 0.92);
    });
}

export async function loadImageBitmapFallback(blob: Blob): Promise<HTMLImageElement> {
    const url = URL.createObjectURL(blob);
    try {
        const img = new Image();
        img.src = url;
        await img.decode();
        return img;
    } finally {
        URL.revokeObjectURL(url);
    }
}
